import { TrendingDown, TrendingUp } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { cn } from '@/lib/utils'
import type { Stat } from '@/models'

/**
 * A labelled figure in a card: the shape Overview, Revenue, Leaderboard, Gift
 * Codes and a player's profile all reach for. It lives in `shared/` rather than
 * `overview/` because five pages is not one page's component.
 *
 * The label is sentence-case sans, not an uppercase mono `t-label`. A stat
 * card is read as a sentence — "Gross revenue: $425,366" — and the uppercase
 * treatment belongs to column headers, where it marks a machine-sorted axis.
 * Keeping the figure at `text-xl` rather than `text-2xl` lets four of these sit
 * in a row without shouting over the page title above them.
 *
 * The footnote is either a trend against the previous period (`delta` + `up`,
 * which draws an arrow) or a plain line of context (`meta`). Never both — an
 * arrow next to a number that is not a comparison is a lie about the data.
 */
export function StatCard({ stat }: { stat: Stat }) {
  const Arrow = stat.up ? TrendingUp : TrendingDown

  return (
    <Card className="hover:border-border-strong px-5 py-4 transition-colors duration-100 ease-out">
      <div className="flex items-center justify-between gap-2">
        <p className="text-fg-subtle m-0 truncate text-sm font-medium">{stat.label}</p>
        {stat.live && (
          <span className="t-tag text-ok flex shrink-0 items-center gap-1">
            <span aria-hidden className="bg-ok size-1.5 animate-pulse rounded-full" />
            Live
          </span>
        )}
      </div>

      <p className="text-fg mt-1.5 mb-0 truncate text-xl font-bold tabular-nums">{stat.value}</p>

      {stat.delta ? (
        <div
          className={cn(
            'mt-2 flex items-center gap-1 text-xs font-semibold',
            stat.up ? 'text-ok' : 'text-err',
          )}
        >
          <Arrow aria-hidden className="size-3.5 shrink-0" />
          <span className="tabular-nums">{stat.delta}</span>
          <span className="text-fg-subtle truncate font-normal">vs yesterday</span>
        </div>
      ) : stat.meta ? (
        <p className="text-fg-subtle mt-2 mb-0 truncate text-xs tabular-nums">{stat.meta}</p>
      ) : null}
    </Card>
  )
}
